import {
  AwardIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RupeeIcon,
  TextInput,
} from '@razorpay/blade/components';
import useFetchPayDetailsData from 'components/PayDetails/hooks/useFetchPayDetailsData';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import { useForm } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React from 'react';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { PerquisitesFormData } from '../types';

// TODO: get options from API
const options = [
  {
    id: '1',
    name: 'Car Rental',
    description: 'Perquisite for transportation needs',
  },
  {
    id: '2',
    name: 'Housing',
    description: 'Accommodation benefit',
  },
  {
    id: '3',
    name: 'Health Insurance',
    description: 'Medical coverage benefit',
  },
];

const PerquisitesModal = ({
  initialFormData,
  isOpen,
  onDismiss,
  onSubmit,
}: {
  initialFormData: PerquisitesFormData;
  isOpen: boolean;
  onDismiss: () => void;
  onSubmit: () => void;
}) => {
  const { formData, setFieldValue, register } = useForm<PerquisitesFormData>(initialFormData);
  const { urlParams } = useSearchParams();
  const userId = urlParams.get('userId');
  const isEdit = initialFormData.componentId !== '';
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const peopleId = String(userId ?? loggedInUserId);
  const { firstName } = useFetchPayDetailsData(peopleId);

  const onChange = (value: string) => {
    setFieldValue('componentId', value);
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="medium">
      <ModalHeader
        title={`${isEdit ? 'Edit' : 'Add'} Perquisites for ${firstName}`}
        subtitle={'Perks that employees enjoy over and above their salary'}
        leading={
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            backgroundColor={'surface.background.sea.subtle'}
            padding={'spacing.3'}
            borderRadius={'round'}
            marginTop={'spacing.4'}>
            <AwardIcon color="surface.icon.onSea.onIntense" />
          </Box>
        }
      />
      <ModalBody padding="spacing.6">
        <Box
          padding={'spacing.8'}
          display="flex"
          flexDirection="column"
          gap="spacing.5"
          backgroundColor={'surface.background.gray.moderate'}
          borderRadius={'xlarge'}>
          <AutoCompleteV2
            label="Perquisites"
            placeholder="Search for a component"
            options={options}
            onChange={(item) => onChange(item.id)}
            value={formData.componentId ?? ''}
            itemToLabel={(item) => item.name}
            itemToKey={(item) => item.id}
            itemToDescription={(item) => item.description}
            necessityIndicator={'required'}
            width={'680px'}
          />
          <Box display="flex" gap="spacing.5" justifyContent="space-between">
            <Box width={'100%'}>
              <TextInput
                label="Total Value (annual)"
                necessityIndicator={'required'}
                placeholder="Enter amount"
                leadingIcon={RupeeIcon}
                value={formData.totalValue ? formData.totalValue.toString() : ''}
                onChange={(e) => {
                  const value = e.value || '';
                  setFieldValue('totalValue', value === '' ? 0 : parseFloat(value));
                }}
              />
            </Box>
            <Box width={'100%'}>
              <TextInput
                label="Amount Paid by Employee"
                necessityIndicator={'required'}
                placeholder="Enter amount"
                leadingIcon={RupeeIcon}
                value={formData.amountPaidByEmployee ? formData.amountPaidByEmployee.toString() : ''}
                onChange={(e) => {
                  const value = e.value || '';
                  setFieldValue('amountPaidByEmployee', value === '' ? 0 : parseFloat(value));
                }}
              />
            </Box>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.5">
          <Button variant="tertiary" onClick={onDismiss}>
            Back
          </Button>
          <Button variant="secondary" onClick={onSubmit}>
            {isEdit ? 'Modify Perquisite' : 'Add Perquisite'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default PerquisitesModal;
